/**
 * Mouse Controls Manager
 * Handles mouse button input with proper encapsulation and cleanup
 * Follows best practices: no memory leaks, single responsibility, type safety
 */

import { frontendLogger } from '../../shared/logger';

export interface MouseControlsOptions {
  onFire?: (firing: boolean) => void;
  onADS?: (ads: boolean) => void;
}

export class MouseControls {
  // Private state
  private _isFiring = false;
  private _isADS = false;
  private readonly onFire?: (firing: boolean) => void;
  private readonly onADS?: (ads: boolean) => void;

  // Bound event handlers for cleanup
  private readonly boundHandlers = {
    mouseDown: this.handleMouseDown.bind(this),
    mouseUp: this.handleMouseUp.bind(this),
    contextMenu: this.handleContextMenu.bind(this)
  };

  constructor(options: MouseControlsOptions = {}) {
    this.onFire = options.onFire;
    this.onADS = options.onADS;
    this.setupEventListeners();
    frontendLogger.info('MouseControls initialized');
  }

  // Public getters for read-only state access
  public get isFiring(): boolean { return this._isFiring; }
  public get isADS(): boolean { return this._isADS; }

  private setupEventListeners(): void {
    document.addEventListener('mousedown', this.boundHandlers.mouseDown);
    document.addEventListener('mouseup', this.boundHandlers.mouseUp);
    document.addEventListener('contextmenu', this.boundHandlers.contextMenu);
  }

  private handleMouseDown(e: MouseEvent): void {
    // Left click - fire weapon
    if (e.button === 0) {
      e.preventDefault();
      if (!this._isFiring) {
        this._isFiring = true;
        this.onFire?.(true);
        frontendLogger.debug('Started firing');
      }
    }

    // Right click - Aim Down Sights (ADS)
    if (e.button === 2) {
      e.preventDefault();
      if (!this._isADS) {
        this._isADS = true;
        this.onADS?.(true);
        frontendLogger.debug('ADS enabled');
      }
    }
  }

  private handleMouseUp(e: MouseEvent): void {
    // Left click release - stop firing
    if (e.button === 0) {
      e.preventDefault();
      if (this._isFiring) {
        this._isFiring = false;
        this.onFire?.(false);
        frontendLogger.debug('Stopped firing');
      }
    }

    // Right click release - disable ADS
    if (e.button === 2) {
      e.preventDefault();
      if (this._isADS) {
        this._isADS = false;
        this.onADS?.(false);
        frontendLogger.debug('ADS disabled');
      }
    }
  }

  private handleContextMenu(e: MouseEvent): void {
    // Prevent context menu in game (especially when pointer locked)
    e.preventDefault();
  }

  /**
   * Force stop firing (e.g., when losing pointer lock)
   */
  public stopFiring(): void {
    if (this._isFiring) {
      this._isFiring = false;
      this.onFire?.(false);
      frontendLogger.debug('Firing stopped (forced)');
    }
  }

  /**
   * Force disable ADS
   */
  public disableADS(): void {
    if (this._isADS) {
      this._isADS = false;
      this.onADS?.(false);
      frontendLogger.debug('ADS disabled (forced)');
    }
  }

  /**
   * Reset all mouse states
   */
  public reset(): void {
    this._isFiring = false;
    this._isADS = false;
    frontendLogger.debug('Mouse controls reset');
  }

  /**
   * Cleanup method to remove event listeners and prevent memory leaks
   */
  public destroy(): void {
    document.removeEventListener('mousedown', this.boundHandlers.mouseDown);
    document.removeEventListener('mouseup', this.boundHandlers.mouseUp);
    document.removeEventListener('contextmenu', this.boundHandlers.contextMenu);
    this.reset();
    frontendLogger.info('MouseControls destroyed');
  }

  /**
   * Get debug info about current mouse state
   */
  public getDebugInfo(): {
    isFiring: boolean;
    isADS: boolean;
  } {
    return {
      isFiring: this._isFiring,
      isADS: this._isADS
    };
  }
}
